import matter from 'gray-matter';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';
import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import React, { useEffect } from 'react';

import learnSqlPositions from '../../../post-positions/learn-sql';
import ROUTES from '../../../routes';

import AppGraphic from '@components/AppGraphic';
import Container from '@components/Container';
import CustomerLogos from '@components/CustomerLogos';
import CustomHead from '@components/CustomHead';
import Footer from '@components/FooterNew';
import Header from '@components/HeaderNew';
import HeroBottom from '@components/HeroBottom';
import FirstSection from '@components/landing/FirstSection';
import FourthSection from '@components/landing/FourthSection';
import Hero from '@components/landing/Hero';
import SecondSection from '@components/landing/SecondSection';
import ThirdSection from '@components/landing/ThirdSection';
import LinkList from '@components/LinkList';
import Section from '@components/Section';
import { getConnectionPages } from '@utils/airtableClient';
import { revalidate } from '@utils/constants';
import { replaceAttachments } from '@utils/s3';

interface Params extends ParsedUrlQuery {
  connection: string;
}

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  const pages = await getConnectionPages();
  const filteredPages = pages.filter((p) => p.connection);

  return {
    fallback: false,
    paths: filteredPages.map((page) => ({
      params: {
        connection: page.connection,
      },
    })),
  };
};

type Record = Awaited<ReturnType<typeof getConnectionPages>>[number];
interface Toggle {
  content: string;
  emoji: string;
  heading: string;
  link: {
    asPath: string;
    href: string;
    text: string;
  };
}

export const getStaticProps: GetStaticProps<
  { data: Record & { sec1Toggles: Toggle[] }; guides },
  Params
> = async (ctx) => {
  try {
    const pages = await getConnectionPages();
    const lookForRecord = pages.find(
      (item) => item.connection === `${ctx.params.connection}`,
    );
    const data = {
      ...lookForRecord,
      sec1Toggles: [],
    };
    replaceAttachments('connections', () => data.connection, data);

    const guides = ((context) => {
      const keys = context.keys();
      const values = keys.map(context) as { get default(): string }[];
      const guidesData = keys.map((key, index) => {
        // Create id from filename
        const id = key.split('/')[1];
        if (id === data.database_key) {
          const value = values[index];
          // Parse yaml metadata & markdownbody in document
          const document = matter(value.default);
          const post_information = JSON.stringify(document.data);

          // Create slug from filename
          const slug = key
            .replace(/^.*[\\/]/, '')
            .split('.')
            .slice(0, -1)
            .join('.');

          return {
            post_information,
            slug,
            id,
            position: learnSqlPositions.indexOf(slug),
            isDraft:
              document.data.draft === undefined ? true : document.data.draft,
          };
        }
        return null;
      });

      return guidesData
        .filter((g) => (g ? !g.isDraft : false))
        .sort((a, b) => (a.position < b.position ? -1 : 1));
    })(require.context('../../../data/learn-sql', true, /\.md$/));
    for (let i = 1; ; i++) {
      if (!data[`sec1_h3_${i}`]) {
        break;
      }
      data.sec1Toggles.push({
        heading: data[`sec1_h3_${i}`],
        emoji: data[`sec1_icon${i}`],
        content: data[`sec1_content${i}`],
        link: {
          text: data[`sec1_link${i}_text`],
          href: data[`sec1_link${i}_slug`],
          asPath: data[`sec1_link${i}_slug`],
        },
      });
    }
    return {
      props: {
        data,
        guides,
      },
      revalidate,
    };
  } catch (error) {
    console.error();
    return {
      props: {
        data: null,
        err: 'Something went wrong 😕',
        guides: null,
      },
    };
  }
};

const Home = ({
  data,
  guides,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter();
  const { features } = router.query;

  const guidesList = guides?.slice(0, 8)?.map((g) => {
    const { title } = JSON.parse(g.post_information);
    return {
      title,
      href: ROUTES.learnPost().href,
      asPath: ROUTES.learnPost(data.database_key, g.slug).asPath,
    };
  });

  useEffect(() => {
    if (features === 'true') {
      // Go to features
      document.querySelector('#features')?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [features]);

  return (
    <>
      <CustomHead
        meta={{
          title: `${data.tab_title}`,
          description: `${data.meta_description}`,
        }}
        openGraph={undefined}
      />

      <main>
        <Header />
        <Section>
          <Hero
            title={data.title}
            // @ts-expect-error error with Hero being JS
            customLogo={`${process.env.NEXT_PUBLIC_ASSET_HOST}/static/landing/first-section/${data.database_key}.svg`}
            // @ts-expect-error error with Hero being JS
            customCta={{
              text: 'Go to connection guide',
              href: ROUTES.docsPost().href,
              asPath: ROUTES.docsPost(
                'connection-guides',
                `connecting-to-${data.database_key}`,
              ).asPath,
            }}
            disableCustomers
          />

          <AppGraphic />
        </Section>

        <CustomerLogos />

        {guidesList?.length ? (
          <Container singleColumn className="mt-16 mb-8">
            <LinkList
              title={`${data.database_name} Tutorials`}
              items={guidesList}
              containerClasses="relative z-20 md:grid-cols-2 md:col-gap-10 md:row-gap-4"
            />
          </Container>
        ) : null}
        <FirstSection heading={data.sec1_h2} toggles={data.sec1Toggles} />
        <SecondSection heading={data.sec2_h2} />
        <ThirdSection heading={data.sec3_h2} />
        <FourthSection heading={data.sec4_h2} />

        <HeroBottom
          icon={`${process.env.NEXT_PUBLIC_ASSET_HOST}/static/homepage/popsql_app_icon.png`}
          title={'The SQL editor for team collaboration'}
          description=""
        />
      </main>
      <Footer />
    </>
  );
};

export default Home;
